// Login.jsx
import React, { useState, useEffect } from 'react';

// Parse users from environment variables
const getAuthorizedUsers = () => {
  const users = [];
  
  // Method 1: Individual user variables
  for (let i = 1; i <= 10; i++) {
    const userVar = process.env[`REACT_APP_USER_${i}`];
    if (userVar) {
      const [email, hashedPassword] = userVar.split(':');
      if (email && hashedPassword) {
        users.push({ 
          email: email.toLowerCase().trim(), 
          hashedPassword: hashedPassword.trim(),
          isAdmin: i === 2 || email === 'info@afrinextventures.com' // Set user 2 as admin
        });
        console.log(`Loaded user: ${email} with hash: ${hashedPassword}, isAdmin: ${i === 2}`);
      }
    }
  }
  
  // Method 2: JSON format
  if (process.env.REACT_APP_USERS_JSON) {
    try {
      const jsonUsers = JSON.parse(process.env.REACT_APP_USERS_JSON);
      jsonUsers.forEach(user => {
        users.push({
          email: user.email.toLowerCase().trim(),
          hashedPassword: user.passwordHash || user.password,
          isAdmin: user.isAdmin || false
        });
      });
    } catch (e) {
      console.error('Error parsing users JSON:', e);
    }
  }
  
  console.log('Total authorized users loaded:', users.length);
  return users;
};

// Password verification function
const verifyPassword = (plainPassword, storedHash) => {
  // Hash the entered password using base64
  const hashedEntered = btoa(plainPassword);
  console.log('Verifying password:');
  console.log('- Entered password:', plainPassword);
  console.log('- Entered password hash:', hashedEntered);
  console.log('- Stored hash:', storedHash);
  console.log('- Match:', hashedEntered === storedHash);
  return hashedEntered === storedHash;
};

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);

  const authorizedUsers = getAuthorizedUsers();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Clear debug info when user types
    if (debugInfo) setDebugInfo(null);
  };

  const authenticateUser = async (email, password) => {
    // Find user by email
    const user = authorizedUsers.find(
      u => u.email === email.toLowerCase().trim()
    );
    
    console.log('Looking for email:', email.toLowerCase().trim());
    console.log('Found user:', user);
    
    if (!user) {
      console.log('User not found in authorized list');
      return { success: false, message: 'Invalid email or password' };
    }
    
    // Verify password
    const isValidPassword = verifyPassword(password, user.hashedPassword);
    
    if (isValidPassword) {
      return { 
        success: true, 
        user: { 
          email: user.email,
          isAdmin: user.isAdmin || false
        },
        message: 'Login successful!' 
      };
    }
    
    return { success: false, message: 'Invalid email or password' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLocked) {
      setErrors({ submit: 'Account temporarily locked. Please try again later.' });
      return;
    }
    
    if (!formData.email || !formData.password) {
      setErrors({ submit: 'Please enter both email and password' });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await authenticateUser(formData.email, formData.password);
      
      if (result.success) {
        setLoginSuccess(true);
        localStorage.setItem('user', JSON.stringify(result.user));
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('isAdmin', result.user.isAdmin ? 'true' : 'false');
        localStorage.setItem('loginTime', new Date().toISOString());
        
        setAttempts(0);
        setIsLocked(false);
        
        // Redirect based on user role
        setTimeout(() => {
          if (result.user.isAdmin) {
            window.location.href = '/blogwrite';
          }
        }, 1500);
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        
        if (newAttempts >= 5) {
          setIsLocked(true);
          setErrors({ submit: 'Too many failed attempts. Account locked for 15 minutes.' });
          setTimeout(() => {
            setIsLocked(false);
            setAttempts(0);
          }, 900000);
        } else {
          setErrors({ submit: `${result.message} (${5 - newAttempts} attempts remaining)` });
        }
        
        // Show debug info in development
        if (process.env.NODE_ENV === 'development') {
          const user = authorizedUsers.find(u => u.email === formData.email.toLowerCase());
          if (user) {
            setDebugInfo({
              enteredHash: btoa(formData.password),
              storedHash: user.hashedPassword,
              email: formData.email,
              isAdmin: user.isAdmin
            });
          }
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ submit: 'Login failed. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loginSuccess) {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-fourth to-third p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome Back!</h2>
          <p className="text-gray-600 mb-4">Successfully logged in as {formData.email}</p>
          {user.isAdmin && (
            <div className="mb-3 inline-block bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold">
              👑 Administrator Access
            </div>
          )}
          <p className="text-gray-500 text-sm">Redirecting to dashboard...</p>
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div className="bg-purple-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-fourth to-third p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-third rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-second" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-800">Secure Login</h2>
          <p className="text-gray-600 mt-2">Enter your credentials to access</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {/* Email Field */}
          <div className="mb-4">
            <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
              Email Address *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              </div>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-third transition-all
                  ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Enter your email"
                autoComplete="email"
                disabled={isLocked}
              />
            </div>
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Password Field */}
          <div className="mb-2">
            <label htmlFor="password" className="block text-gray-700 font-medium mb-2">
              Password *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full pl-10 pr-12 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-third transition-all
                  ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={isLocked}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password}</p>
            )}
          </div>

          {/* Forgot Password Link */}
          <div className="mb-6 text-right">
            <a href="/forgot-password" className="text-ss text-second hover:underline">
              Forgot password?
            </a>
          </div>

          {/* Attempts remaining */}
          {attempts > 0 && !isLocked && (
            <div className="mb-4 text-sm text-orange-600">
              ⚠️ {5 - attempts} login attempts remaining
            </div>
          )}
          
          {/* Lock message */}
          {isLocked && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              🔒 Account temporarily locked. Please try again later.
            </div>
          )}

          {/* Submit Error */}
          {errors.submit && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {errors.submit}
            </div>
          )}

          {/* Debug Info (Development Only) */}
          {debugInfo && process.env.NODE_ENV === 'development' && (
            <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded-lg text-xs">
              <p className="font-bold">Debug Info:</p>
              <p>Email: {debugInfo.email}</p>
              <p>Entered Password Hash: {debugInfo.enteredHash}</p>
              <p>Stored Password Hash: {debugInfo.storedHash}</p>
              <p>Match: {debugInfo.enteredHash === debugInfo.storedHash ? 'Yes' : 'No'}</p>
              <p>Is Admin: {debugInfo.isAdmin ? 'Yes' : 'No'}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || isLocked}
            className="w-full bg-gradient-to-r from-fourth to-third text-tertiary font-semibold py-2 px-4 rounded-lg
              hover:from-tretiary hover:to-fourth transition-all transform hover:scale-[1.02]
              disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-second" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Authenticating...
              </span>
            ) : (
              'Login'
            )}
          </button>
        </form>

        {/* Security Notice */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <span className="font-semibold">🔐 Secure Access</span>
              <br />
              This system uses individual passwords for each authorized email.
              <br />
              <span className="text-blue-600">Contact administrator for access.</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;